import { QaItem, QaSection, SiblingInfo } from './types';

export const formatSiblingCurrency = (
  value: number | undefined | null,
): string => {
  const num = Number(value ?? 0);
  if (Number.isNaN(num)) return '$0';
  return `$${num.toLocaleString('en-US')}`;
};

export const formatSiblingDetailLine = (
  sibling: SiblingInfo,
  index: number,
): string => {
  const inForceLabel = sibling.hasExistingCoverage ? 'Yes' : 'No';
  return [
    `Sibling ${index + 1}: ${sibling.fullName}`,
    `Age: ${sibling.age}`,
    `Insurance in force: ${inForceLabel}`,
    `inforce: ${formatSiblingCurrency(sibling.inforce)}`,
    `applying: ${formatSiblingCurrency(sibling.pending)}`,
  ].join(', ');
};

export const mapQaSections = (data: unknown): QaSection[] => {
  if (!data || typeof data !== 'object') return [];
  const interviewExport = data as { interview?: any[] };
  const sections = interviewExport.interview ?? [];
  const answerUuidToText = new Map<string, string>();
  let questionCounter = 0;

  return sections.map(section => ({
    title: section.sectionName ?? 'Untitled Section',
    data: mapAsksToQaItems(
      section.asks ?? [],
      answerUuidToText,
      () => ++questionCounter,
    ),
  }));
};

const mapAsksToQaItems = (
  asks: any[],
  answerUuidToText: Map<string, string>,
  nextQuestionIdx: () => number,
): QaItem[] => {
  const qaItems: QaItem[] = [];

  const handleAsk = (ask: any): void => {
    const idx = nextQuestionIdx();
    const availableOptions = extractAvailableOptions(ask, answerUuidToText);

    ask.answers?.forEach((answer: any) => {
      qaItems.push(
        buildQaItem(ask, answer, idx, availableOptions, answerUuidToText),
      );
      answer?.relatedAsks?.forEach((relatedAsk: any) => handleAsk(relatedAsk));
    });
  };

  asks.forEach(handleAsk);
  return qaItems;
};

const extractAvailableOptions = (
  ask: any,
  answerUuidToText: Map<string, string>,
): string[] | null =>
  ask?.answerChoices?.length
    ? ask.answerChoices.map((choice: any) => {
        answerUuidToText.set(choice.answerUUID, choice.text);
        return choice.text;
      })
    : null;

const buildQaItem = (
  ask: any,
  answer: any,
  idx: number,
  availableOptions: string[] | null,
  answerUuidToText: Map<string, string>,
): QaItem => ({
  idx,
  question: ask.questionPrompt ?? '',
  answer:
    answer?.answerUUID && answerUuidToText.has(answer.answerUUID)
      ? answerUuidToText.get(answer.answerUUID)!
      : (answer?.value ?? ''),
  duration: ask.duration ?? 0,
  availableOptions,
  siblings: answer?.metadata?.siblings?.length
    ? answer.metadata.siblings
    : null,
});
